import { useEffect, useState } from "react";
import styled from "styled-components";
import { toSchema } from "@lifeat/shared/data-models/definition";
import { SystemManagedDataCollection } from "@lifeat/shared/data-models/system/SystemManagedDataEntity";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { LifeAtTextInputV2 } from "@common/LifeAtTextInput/LifeAtTextInputV2";
import { useDatabaseContext } from "@contexts/DatabaseContext";
import { useToastListContext } from "@contexts/ToastListContext";
import { useEvent } from "@hooks/useEvent";
import { callMonolith } from "@service/ServerlessFunctionsService";
import Styles from "@themes/Styles";

type State = "loading" | "ready" | "editing";

const Root = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const InputContainer = styled.div`
    border: 1px solid ${({ theme }) => theme.borderPrimary};
    border-radius: ${Styles.borderRadiusNormal};
    padding: 4px;

    display: flex;
    flex-direction: row;
    gap: 8px;
`;

const Input = styled(LifeAtTextInputV2)`
    flex: 1;
`;

const Label = styled.span``;

export function AdminCategoryInput() {
    const [textInput, setTextInput] = useState("");
    const [state, setState] = useState<State>("loading");
    const { addToastNotification } = useToastListContext();

    const db = useDatabaseContext();

    const enterEditState = useEvent(() => {
        setState("editing");
    });

    const cancelEditing = useEvent(() => {
        setState("ready");
    });

    const updateActiveCategories = useEvent(async () => {
        setState("loading");
        try {
            const schema = toSchema(SystemManagedDataCollection.at("activeCategories").node.definition);
            const value = schema.parse(JSON.parse(textInput));
            await callMonolith("admin-set-active-categories", { activeCategories: value });
            addToastNotification("Success", "Updated active categories.");
        } catch {
            addToastNotification("Error", "Invalid categories.");
        } finally {
            setState("ready");
        }
    });

    const load = useEvent(async () => {
        const categories = await db.get(SystemManagedDataCollection.at("activeCategories").node);
        setTextInput(JSON.stringify(categories));
        setState("ready");
    });

    useEffect(() => {
        load();
    }, [load]);

    return (
        <Root>
            <Label>Active Categories</Label>
            <InputContainer>
                <Input
                    disabled={state !== "editing"}
                    value={state === "loading" ? "Loading..." : textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                />
                {state === "ready" && <LifeAtButtonV2 onClick={enterEditState}>Edit</LifeAtButtonV2>}
                {state === "editing" && (
                    <>
                        <LifeAtButtonV2 onClick={cancelEditing}>Cancel</LifeAtButtonV2>
                        <LifeAtButtonV2 onClick={updateActiveCategories}>Submit</LifeAtButtonV2>
                    </>
                )}
            </InputContainer>
        </Root>
    );
}
